import { useEffect, useState } from "react";
import type { MutableRefObject } from "react";
import type { SelectedItem } from "../type";

const CLASS_SELECTED_CELL = "!bg-red-200";
const useShiftSelection = (
  refTable: MutableRefObject<HTMLDivElement | null>,
  disabled: boolean,
  onChangedSelected: (data: SelectedItem[]) => void
) => {
  const [selectedCells, setSelectedCells] = useState<SelectedItem[]>([]);
  useEffect(() => {
    if (!refTable.current || disabled) return;

    let isSelecting = false;
    let isCtrlMode = false;
    let startCell: { x: number; y: number } | null = null;
    let cells: Array<{
      element: HTMLDivElement;
      top: number;
      bottom: number;
      left: number;
      right: number;
      indexCol?: string;
      date?: string;
      nurseId?: string;
      shiftType?: string;
      note?: string | null;
      indexRow?: string
    }> = [];

    // store cells selected across multiple selections (persistent)
    let persistentSelected: Record<string, SelectedItem> = {};
    // cells being selected in the current drag operation
    let draftSelected: Record<string, SelectedItem> = {};

    refTable.current.onmousedown = (e: MouseEvent) => {
      const targetEl = e.target as HTMLDivElement;

      const staffScheduleElm = targetEl.closest(
        ".staff_schedule"
      ) as HTMLDivElement | null;

      if (!staffScheduleElm) return;

      isSelecting = true;
      isCtrlMode = e.ctrlKey;

      const rect = staffScheduleElm.getBoundingClientRect();

      startCell = {
        x: (rect.left + rect.right) / 2,
        y: (rect.top + rect.bottom) / 2,
      };

      cells = Array.from(
        refTable.current!.querySelectorAll(".staff_schedule")
      ).map((cell) => {
        const ele = cell as HTMLDivElement;
        const r = ele.getBoundingClientRect();

        return {
          element: ele,
          top: r.top,
          bottom: r.bottom,
          left: r.left,
          right: r.right,
          indexCol: ele.dataset.indexcol,
          indexRow: ele.dataset.indexrow,
          date: ele.dataset.date,
          nurseId: ele.dataset.nurseid,
          shiftType: ele.dataset.shifttype,
          note: ele.dataset.note,
        };
      });

      if (!isCtrlMode) {
        persistentSelected = {};
        cells.forEach((cell) =>
          cell.element.classList.remove(CLASS_SELECTED_CELL)
        );
      }

      draftSelected = {};
      const { indexcol, date, nurseid, shifttype, note , indexrow} =
        staffScheduleElm.dataset;
      if (date && nurseid && indexcol) {
        const key = `${nurseid}_${date}_${indexcol}`;
        draftSelected[key] = {
          indexRow: indexrow || "",
          indexCol: indexcol,
          date,
          nurseId: nurseid,
          shiftType: shifttype as any,
          note,
          key,
        };
      }

      // update temporary highlight (persistent + draft)
      cells.forEach((cell) => {
        const key = `${cell.nurseId}_${cell.date}_${cell.indexCol}`;
        const inPersistent = !!persistentSelected[key];
        const inDraft = !!draftSelected[key];
        if (inPersistent || inDraft) {
          cell.element.classList.add(CLASS_SELECTED_CELL);
        } else {
          cell.element.classList.remove(CLASS_SELECTED_CELL);
        }
      });
    };

    document.onmousemove = (e: MouseEvent) => {
      if (!isSelecting || !startCell) return;

      const x = e.clientX;
      const y = e.clientY;

      const minX = Math.min(x, startCell.x);
      const maxX = Math.max(x, startCell.x);

      const minY = Math.min(y, startCell.y);
      const maxY = Math.max(y, startCell.y);

      const nextDraft: Record<string, SelectedItem> = {};

      cells.forEach((cell) => {
        const inside =
          cell.left < maxX &&
          cell.right > minX &&
          cell.top < maxY &&
          cell.bottom > minY;

        if (inside && cell.date && cell.nurseId && cell.indexCol) {
          const key = `${cell.nurseId}_${cell.date}_${cell.indexCol}`;
          nextDraft[key] = {
            indexCol: cell.indexCol,
            indexRow: cell.indexRow || "",
            date: cell.date,
            nurseId: cell.nurseId,
            shiftType: cell.shiftType as any,
            note: cell.note ?? null,
            key,
          };
        }
      });

      draftSelected = nextDraft;

      // update highlight: persistent + draft
      cells.forEach((cell) => {
        const key = `${cell.nurseId}_${cell.date}_${cell.indexCol}`;
        const inPersistent = !!persistentSelected[key];
        const inDraft = !!draftSelected[key];
        if (inPersistent || inDraft) {
          cell.element.classList.add(CLASS_SELECTED_CELL);
        } else {
          cell.element.classList.remove(CLASS_SELECTED_CELL);
        }
      });
    };

    window.onmouseup = () => {
      if (!isSelecting) return;

      isSelecting = false;

      // On mouse up:
      // - If Ctrl is held: merge draft into persistent
      // - Otherwise: replace persistent with draft
      if (isCtrlMode) {
        persistentSelected = {
          ...persistentSelected,
          ...draftSelected,
        };
      } else {
        persistentSelected = { ...draftSelected };
      }

      const arr = Object.values(persistentSelected);
      if (!isCtrlMode && arr.length) {
        setSelectedCells(arr);
      }

      startCell = null;
      draftSelected = {};
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key !== "Control") return;
      if (isSelecting) return;

      const arr = Object.values(persistentSelected);
      if (arr.length) {
        setSelectedCells(arr);

        // setOpenModal(true);
      }
    };

    window.addEventListener("keyup", handleKeyUp);

    return () => {
      if (refTable.current) refTable.current.onmousedown = null;
      document.onmousemove = null;
      window.onmouseup = null;
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [disabled, selectedCells]);

  useEffect(() => {
    onChangedSelected(selectedCells);
  }, [selectedCells]);

  const removeSelectedCell = () => {
    const selectedCells = Array.from(
      refTable.current!.getElementsByClassName(CLASS_SELECTED_CELL)
    );
    selectedCells.forEach((selectedCell) =>
      selectedCell.classList.remove(CLASS_SELECTED_CELL)
    );
    setSelectedCells([]);
  };
  return { removeSelectedCell };
};

export default useShiftSelection;
